import { api } from "./api";
import { API_ENDPOINTS } from "../../utils/api-service";
import {
  hasOwnKey,
  toNullableStr,
  toNumberOr,
  toNumberOrNull,
  toSnakeCaseChangesBody,
  toStr,
  unwrapApiData,
} from "./points-service-helpers";
import {
  CreatePointRulePayload,
  DeactivatePointRulePayload,
  POINT_RULE_ACTION_LABELS,
  POINT_RULE_KIND_LABELS,
  POINT_WINDOW_PERIOD_LABELS,
  PointRule,
  PointRuleActionType,
  PointRuleChanges,
  PointRuleEditableField,
  PointRuleKind,
  PointStreakConfig,
  PointStreakMilestone,
  PointWindowPeriod,
  SavePointStreakPayload,
  UpdatePointRulePayload,
} from "../../models/pointRule";

/** camelCase de PointRuleChanges → snake_case del body. También es la lista blanca de campos que viajan. */
const POINT_RULE_CHANGE_BODY_KEYS: Record<PointRuleEditableField, string> = {
  pointsValue: "points_value",
  active: "active",
  visibleToUser: "visible_to_user",
  displayTitle: "display_title",
  displayDescription: "display_description",
  iconKey: "icon_key",
  windowPeriod: "window_period",
  windowStartsOn: "window_starts_on",
  windowEndsOn: "window_ends_on",
  thresholdCount: "threshold_count",
  streakPeriods: "streak_periods",
  freeEventsOnly: "free_events_only",
  eventTypeId: "event_type_id",
  eventSubTypeId: "event_sub_type_id",
};

const pointRuleTag = (id: number) => ({ type: "pointRules" as const, id });
const pointRuleListTag = { type: "pointRules" as const, id: "LIST" };
const pointStreakTag = { type: "pointRules" as const, id: "STREAK" };

const isPointRuleActionType = (value: unknown): value is PointRuleActionType =>
  hasOwnKey(POINT_RULE_ACTION_LABELS, value);

const isPointRuleKind = (value: unknown): value is PointRuleKind =>
  hasOwnKey(POINT_RULE_KIND_LABELS, value);

const isPointWindowPeriod = (value: unknown): value is PointWindowPeriod =>
  hasOwnKey(POINT_WINDOW_PERIOD_LABELS, value);

const isPointRuleEditableField = (value: unknown): value is PointRuleEditableField =>
  hasOwnKey(POINT_RULE_CHANGE_BODY_KEYS, value);

// Fuera de contrato no hay un valor correcto que mostrar: el default evita romper la lista entera.
const DEFAULT_ACTION_TYPE: PointRuleActionType = "checkin";
const DEFAULT_RULE_KIND: PointRuleKind = "atomic";

export const mapPointRule = (raw: unknown): PointRule => {
  const row = (typeof raw === "object" && raw !== null ? raw : {}) as Record<string, unknown>;
  const rawEditableFields: unknown[] = Array.isArray(row.editable_fields) ? row.editable_fields : [];

  return {
    id: toNumberOr(row.id, 0),
    tenantId: toStr(row.tenant_id),
    actionType: isPointRuleActionType(row.action_type) ? row.action_type : DEFAULT_ACTION_TYPE,
    ruleKind: isPointRuleKind(row.rule_kind) ? row.rule_kind : DEFAULT_RULE_KIND,
    baseRuleId: toNumberOrNull(row.base_rule_id),
    pointsValue: toNumberOrNull(row.points_value),
    windowPeriod: isPointWindowPeriod(row.window_period) ? row.window_period : null,
    windowStartsOn: toNullableStr(row.window_starts_on),
    windowEndsOn: toNullableStr(row.window_ends_on),
    thresholdCount: toNumberOr(row.threshold_count, 0),
    streakPeriods: toNumberOr(row.streak_periods, 0),
    freeEventsOnly: row.free_events_only === true,
    eventTypeId: toNumberOrNull(row.event_type_id),
    eventSubTypeId: toNumberOrNull(row.event_sub_type_id),
    displayTitle: toNullableStr(row.display_title),
    displayDescription: toNullableStr(row.display_description),
    iconKey: toNullableStr(row.icon_key),
    visibleToUser: row.visible_to_user === true,
    active: row.active === true,
    effectiveFrom: toStr(row.effective_from),
    updatedAt: toStr(row.updated_at),
    isPenalty: row.is_penalty === true,
    hasAwardedPoints: row.has_awarded_points === true,
    editableFields: rawEditableFields.filter(isPointRuleEditableField),
    stackedWithRuleId: toNumberOrNull(row.stacked_with_rule_id),
  };
};

const mapPointStreakMilestone = (raw: unknown): PointStreakMilestone => {
  const row = (typeof raw === "object" && raw !== null ? raw : {}) as Record<string, unknown>;
  return {
    id: toNumberOr(row.id, 0),
    streakPeriods: toNumberOr(row.streak_periods, 0),
    pointsValue: toNumberOrNull(row.points_value),
    active: row.active === true,
    hasAwardedPoints: row.has_awarded_points === true,
  };
};

export const mapPointStreakConfig = (raw: unknown): PointStreakConfig => {
  const row = (typeof raw === "object" && raw !== null ? raw : {}) as Record<string, unknown>;
  const milestones: unknown[] = Array.isArray(row.milestones) ? row.milestones : [];
  return {
    intervalWeeks: toNumberOrNull(row.interval_weeks),
    displayTitle: toStr(row.display_title),
    displayDescription: toNullableStr(row.display_description),
    iconKey: toNullableStr(row.icon_key),
    visibleToUser: row.visible_to_user !== false,
    milestones: milestones
      .map(mapPointStreakMilestone)
      .sort((first, second) => first.streakPeriods - second.streakPeriods),
  };
};

/**
 * Body snake_case de PUT/POST: solo los campos de la lista blanca que tienen valor
 * (null sí viaja, undefined no). Nunca salen action_type, rule_kind, tenant_id,
 * effective_from ni id: el backend responde 400 si llegan en un PUT.
 */
export const toPointRuleChangesBody = (changes: PointRuleChanges): Record<string, unknown> =>
  toSnakeCaseChangesBody(changes, POINT_RULE_CHANGE_BODY_KEYS);

export const pointRulesApi = api.injectEndpoints({
  endpoints: (build) => ({
    getPointRules: build.query<PointRule[], string>({
      query: (tenantId) => ({
        url: API_ENDPOINTS.RULES.POINTS.BASE,
        params: { tenant_id: tenantId },
      }),
      transformResponse: (response: unknown): PointRule[] => {
        const rows = unwrapApiData(response);
        return Array.isArray(rows) ? rows.map((row: unknown) => mapPointRule(row)) : [];
      },
      providesTags: (result) => [
        ...(result ?? []).map(({ id }) => pointRuleTag(id)),
        pointRuleListTag,
      ],
    }),

    updatePointRule: build.mutation<PointRule, UpdatePointRulePayload>({
      query: ({ id, changes }) => ({
        url: API_ENDPOINTS.RULES.POINTS.BY_ID(id),
        method: "PUT",
        body: toPointRuleChangesBody(changes),
      }),
      transformResponse: (response: unknown) => mapPointRule(unwrapApiData(response)),
      // La regla que devuelve el PUT entra al cache de la lista apenas responde: si no, entre el
      // PUT y el refetch la tabla muestra el estado viejo, y un "Editar" abierto en ese hueco
      // arranca del valor anterior. La invalidación sigue: el refetch confirma contra la base.
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data: updated } = await queryFulfilled;
          dispatch(
            pointRulesApi.util.updateQueryData("getPointRules", updated.tenantId, (draft) => {
              const index = draft.findIndex((rule) => rule.id === updated.id);
              if (index !== -1) draft[index] = updated;
            })
          );
        } catch {
          // El error lo muestra quien disparó la mutación; el cache queda como estaba.
        }
      },
      invalidatesTags: (_result, _error, arg) => [
        pointRuleTag(arg.id),
        pointRuleListTag,
      ],
    }),

    /**
     * Baja lógica (`DELETE /bo/rules/points/:id`): la regla deja de acreditar y de listarse,
     * pero sigue en la base para que el ledger que ya pagó siga explicándose.
     *
     * La fila sale del cache de la lista apenas responde, además de invalidar: entre el DELETE
     * y el refetch la tabla seguía mostrando una regla que ya no existe, y el modal de baja
     * se podía volver a abrir sobre ella. La respuesta trae la regla dada de baja (el contrato
     * es idempotente: dar de baja algo ya dado de baja devuelve 200 con su estado actual), y
     * justamente por eso NO se escribe en el cache: lo que corresponde es sacarla.
     */
    deactivatePointRule: build.mutation<PointRule, DeactivatePointRulePayload>({
      query: ({ id }) => ({
        url: API_ENDPOINTS.RULES.POINTS.BY_ID(id),
        method: "DELETE",
      }),
      transformResponse: (response: unknown) => mapPointRule(unwrapApiData(response)),
      async onQueryStarted({ id, tenantId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            pointRulesApi.util.updateQueryData("getPointRules", tenantId, (draft) => {
              const index = draft.findIndex((rule) => rule.id === id);
              if (index !== -1) draft.splice(index, 1);
            })
          );
        } catch {
          // El error lo muestra quien disparó la mutación; el cache queda como estaba.
        }
      },
      invalidatesTags: (_result, _error, arg) => [pointRuleTag(arg.id), pointRuleListTag],
    }),

    createPointRule: build.mutation<PointRule, CreatePointRulePayload>({
      query: ({ tenantId, actionType, ruleKind, baseRuleId, ...changes }) => ({
        url: API_ENDPOINTS.RULES.POINTS.BASE,
        method: "POST",
        body: {
          tenant_id: tenantId,
          action_type: actionType,
          rule_kind: ruleKind,
          ...(baseRuleId !== undefined ? { base_rule_id: baseRuleId } : {}),
          ...toPointRuleChangesBody(changes),
        },
      }),
      transformResponse: (response: unknown) => mapPointRule(unwrapApiData(response)),
      invalidatesTags: [pointRuleListTag],
    }),

    getPointStreak: build.query<PointStreakConfig, string>({
      query: (tenantId) => ({
        url: API_ENDPOINTS.RULES.POINTS.STREAK,
        params: { tenant_id: tenantId },
      }),
      transformResponse: (response: unknown) => mapPointStreakConfig(unwrapApiData(response)),
      providesTags: [pointStreakTag],
    }),

    savePointStreak: build.mutation<PointStreakConfig, SavePointStreakPayload>({
      query: ({ tenantId, intervalWeeks, displayTitle, displayDescription, iconKey, visibleToUser, milestones }) => ({
        url: API_ENDPOINTS.RULES.POINTS.STREAK,
        method: "PUT",
        body: {
          tenant_id: tenantId,
          interval_weeks: intervalWeeks,
          display_title: displayTitle,
          display_description: displayDescription,
          icon_key: iconKey,
          visible_to_user: visibleToUser,
          milestones: milestones.map((milestone) => ({
            points_value: milestone.pointsValue,
            active: milestone.active,
          })),
        },
      }),
      transformResponse: (response: unknown) => mapPointStreakConfig(unwrapApiData(response)),
      invalidatesTags: [pointStreakTag, pointRuleListTag],
    }),
  }),
});

export const {
  useGetPointRulesQuery,
  useUpdatePointRuleMutation,
  useCreatePointRuleMutation,
  useDeactivatePointRuleMutation,
  useGetPointStreakQuery,
  useSavePointStreakMutation,
} = pointRulesApi;
